import { useState, type FormEvent } from 'react';
import type {
  ConfidenceLevel,
  EmployeeProfileResponse,
  PotentialAssessmentResponse,
  PotentialTier,
  ReadinessAssessmentResponse,
  ReadinessLevel,
} from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { FactSection } from './FactSection';
import {
  CONFIDENCE_COPY,
  CONFIDENCE_OPTIONS,
  formatTimestamp,
  POTENTIAL_TIER_LABEL,
  POTENTIAL_TIER_OPTIONS,
  READINESS_LEVEL_LABEL,
  READINESS_LEVEL_OPTIONS,
} from './attributeDisplay';
import factStyles from './CaptureSection.module.css';
import formStyles from './AssessmentForm.module.css';

/**
 * Potential and Readiness — capture (`docs/DOMAIN_DECISIONS.md`,
 * "Potential/Readiness Assessment Capture Scope").
 *
 * ---------------------------------------------------------------------
 * TWO SECTIONS, NEVER ONE COMBINED SCORE
 *
 * `docs/PRODUCT_CONSTITUTION.md` §8/§12 and `docs/UX_ARCHITECTURE.md` §8
 * are explicit: Potential, Readiness, Trajectory and Aspiration are never
 * merged into one widget or one label. This file renders two independent
 * `FactSection`s for that reason, not for lack of a shared component — a
 * combined "Potential & Readiness" card would be exactly the collapsing
 * both documents forbid.
 *
 * READINESS IS CURRENT-ROLE ONLY, LIKE ROLE FIT
 *
 * There is no role picker here, mirroring `RoleFitSection`'s own
 * boundary: `docs/MVP_SCOPE.md` scopes this capture increment to what
 * already surfaces via Overview/Career, and a general "assess readiness
 * for any role" browser is exactly the capability-browsing UI this
 * increment's mission explicitly excludes. An employee with no current
 * role has nothing to be ready *for*, so no request is made at all —
 * copied directly from `RoleFitSection`'s own handling of the same fact.
 *
 * WRITING IS MANAGER + HR/ADMIN, NEVER SELF
 *
 * `profile.viewerCanRecordPotentialReadiness` is the only basis on which
 * either "Record"/"Correct" affordance is offered — computed by the API
 * (`api/src/authorization/scope.ts` `canRecordPotentialReadiness`), never
 * guessed at here. This component only decides whether offering the
 * affordance would be honest; the write route re-derives and re-checks
 * independently regardless of what this flag said.
 *
 * CORRECTION, NOT EDITING — THE SAME DISCIPLINE AS ATTRIBUTE ASSESSMENTS
 *
 * "Correct" always sends the current record's id as `supersedesId`; the
 * server records a new row and keeps the original exactly as it was
 * (correction lineage, `docs/DOMAIN_DECISIONS.md`). Every successful
 * mutation re-reads its resource rather than patching local state, the
 * same "re-read, never adjust" discipline every other write path in this
 * application already follows.
 * ---------------------------------------------------------------------
 */
export function PotentialReadinessSection({ profile }: { profile: EmployeeProfileResponse }) {
  const canWrite = profile.viewerCanRecordPotentialReadiness;
  const currentRole = profile.currentRole;

  const potential = useApiResource<PotentialAssessmentResponse | null>(`/employees/${profile.id}/potential`);
  const readiness = useApiResource<ReadinessAssessmentResponse | null>(
    currentRole ? `/employees/${profile.id}/readiness/${currentRole.id}` : null
  );

  const [recordingPotential, setRecordingPotential] = useState(false);
  const [recordingReadiness, setRecordingReadiness] = useState(false);

  return (
    <>
      <FactSection<PotentialAssessmentResponse | null>
        id="potential"
        title="Potential"
        state={potential}
        loadingLabel="Loading potential…"
        errorTitle="Potential could not be loaded"
        isEmpty={(data) => data === null}
        empty={{
          title: 'No potential assessment recorded',
          detail: canWrite
            ? 'Nothing has been recorded yet. Use "Record potential" below to add one.'
            : 'Nothing has been recorded here yet. That is an absence of a record, not a finding about this person.',
        }}
        note="A judged estimate of this person's capacity to develop beyond their current demonstrated capability — an explicit record, never derived from an attribute rating."
      >
        {/* Non-null by construction: `isEmpty` already returned false. */}
        {(data) => (
          <PotentialCard
            potential={data!}
            employeeId={profile.id}
            canWrite={canWrite}
            onChanged={() => potential.reload()}
          />
        )}
      </FactSection>

      {canWrite && potential.status === 'loaded' && potential.data === null ? (
        recordingPotential ? (
          <PotentialForm
            employeeId={profile.id}
            onRecorded={() => {
              setRecordingPotential(false);
              potential.reload();
            }}
            onClose={() => setRecordingPotential(false)}
          />
        ) : (
          <button type="button" className={factStyles.addToggle} onClick={() => setRecordingPotential(true)}>
            Record potential
          </button>
        )
      ) : null}

      <FactSection<ReadinessAssessmentResponse | null>
        id="readiness"
        title={currentRole ? `Readiness for ${currentRole.name}` : 'Readiness'}
        state={currentRole ? readiness : { status: 'loaded', data: null }}
        loadingLabel="Loading readiness…"
        errorTitle="Readiness could not be loaded"
        isEmpty={(data) => data === null}
        empty={{
          title: currentRole ? 'No readiness assessment recorded' : 'No current role is recorded',
          detail: currentRole
            ? canWrite
              ? 'Nothing has been recorded yet. Use "Record readiness" below to add one.'
              : 'Nothing has been recorded here yet. That is an absence of a record, not a finding about this person.'
            : 'Readiness is judged against a specific target role, so there is nothing to compare against. That is an absence of a recorded role assignment, not a finding about this person.',
        }}
        note="A judged estimate of how prepared this person is for the role they currently hold — distinct from Role Fit's mechanical comparison, and never computed from it."
      >
        {(data) => (
          <ReadinessCard
            readiness={data!}
            employeeId={profile.id}
            canWrite={canWrite}
            onChanged={() => readiness.reload()}
          />
        )}
      </FactSection>

      {canWrite && currentRole && readiness.status === 'loaded' && readiness.data === null ? (
        recordingReadiness ? (
          <ReadinessForm
            employeeId={profile.id}
            targetRoleId={currentRole.id}
            onRecorded={() => {
              setRecordingReadiness(false);
              readiness.reload();
            }}
            onClose={() => setRecordingReadiness(false)}
          />
        ) : (
          <button type="button" className={factStyles.addToggle} onClick={() => setRecordingReadiness(true)}>
            Record readiness
          </button>
        )
      ) : null}
    </>
  );
}

function PotentialCard({
  potential,
  employeeId,
  canWrite,
  onChanged,
}: {
  potential: PotentialAssessmentResponse;
  employeeId: string;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [correcting, setCorrecting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  return (
    <div className={factStyles.card}>
      <div className={factStyles.cardHeader}>
        <span className={factStyles.name}>{POTENTIAL_TIER_LABEL[potential.potentialTier]}</span>
        <span className={factStyles.status}>{CONFIDENCE_COPY[potential.confidence].label}</span>
        {canWrite ? (
          <button
            type="button"
            className={factStyles.editToggle}
            onClick={() => {
              setCorrecting((open) => !open);
              setWithdrawing(false);
            }}
          >
            {correcting ? 'Cancel' : 'Correct'}
          </button>
        ) : null}
        {canWrite ? (
          <button
            type="button"
            className={factStyles.editToggle}
            onClick={() => {
              setWithdrawing((open) => !open);
              setCorrecting(false);
            }}
          >
            {withdrawing ? 'Cancel' : 'Withdraw'}
          </button>
        ) : null}
      </div>

      <dl className={factStyles.facts}>
        <div>
          <dt>Recorded</dt>
          <dd>{formatTimestamp(potential.assessedAt)}</dd>
        </div>
      </dl>

      {potential.rationale ? <p className={factStyles.notes}>{potential.rationale}</p> : null}

      {correcting ? (
        <PotentialForm
          employeeId={employeeId}
          correcting={{ assessmentId: potential.id, currentTier: potential.potentialTier, currentConfidence: potential.confidence }}
          onRecorded={() => {
            setCorrecting(false);
            onChanged();
          }}
          onClose={() => setCorrecting(false)}
        />
      ) : null}

      {withdrawing ? (
        <PotentialWithdrawalForm
          employeeId={employeeId}
          potential={potential}
          onWithdrawn={() => {
            setWithdrawing(false);
            onChanged();
          }}
          onClose={() => setWithdrawing(false)}
        />
      ) : null}
    </div>
  );
}

function ReadinessCard({
  readiness,
  employeeId,
  canWrite,
  onChanged,
}: {
  readiness: ReadinessAssessmentResponse;
  employeeId: string;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [correcting, setCorrecting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  return (
    <div className={factStyles.card}>
      <div className={factStyles.cardHeader}>
        <span className={factStyles.name}>{READINESS_LEVEL_LABEL[readiness.readiness]}</span>
        <span className={factStyles.status}>{CONFIDENCE_COPY[readiness.confidence].label}</span>
        {canWrite ? (
          <button
            type="button"
            className={factStyles.editToggle}
            onClick={() => {
              setCorrecting((open) => !open);
              setWithdrawing(false);
            }}
          >
            {correcting ? 'Cancel' : 'Correct'}
          </button>
        ) : null}
        {canWrite ? (
          <button
            type="button"
            className={factStyles.editToggle}
            onClick={() => {
              setWithdrawing((open) => !open);
              setCorrecting(false);
            }}
          >
            {withdrawing ? 'Cancel' : 'Withdraw'}
          </button>
        ) : null}
      </div>

      <dl className={factStyles.facts}>
        <div>
          <dt>Recorded</dt>
          <dd>{formatTimestamp(readiness.assessedAt)}</dd>
        </div>
      </dl>

      {readiness.rationale ? <p className={factStyles.notes}>{readiness.rationale}</p> : null}

      {correcting ? (
        <ReadinessForm
          employeeId={employeeId}
          targetRoleId={readiness.targetRoleId}
          correcting={{ assessmentId: readiness.id, currentLevel: readiness.readiness, currentConfidence: readiness.confidence }}
          onRecorded={() => {
            setCorrecting(false);
            onChanged();
          }}
          onClose={() => setCorrecting(false)}
        />
      ) : null}

      {withdrawing ? (
        <ReadinessWithdrawalForm
          employeeId={employeeId}
          readiness={readiness}
          onWithdrawn={() => {
            setWithdrawing(false);
            onChanged();
          }}
          onClose={() => setWithdrawing(false)}
        />
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------
 * FORMS — shaped after AssessmentForm.tsx's own pattern: local field
 * state, a small submit-state union, and a re-read on success. Neither
 * form asks for a perspective, a date or an assessor — every one of those
 * is derived server-side, the same discipline AssessmentForm documents
 * for attribute assessments.
 * ------------------------------------------------------------------- */

type SubmitState = { status: 'editing' } | { status: 'submitting' } | { status: 'failed'; message: string };

function PotentialForm({
  employeeId,
  correcting,
  onRecorded,
  onClose,
}: {
  employeeId: string;
  correcting?: { assessmentId: string; currentTier: PotentialTier; currentConfidence: ConfidenceLevel };
  onRecorded: () => void;
  onClose: () => void;
}) {
  const [potentialTier, setPotentialTier] = useState<PotentialTier>(correcting?.currentTier ?? 'moderate');
  const [confidence, setConfidence] = useState<ConfidenceLevel>(correcting?.currentConfidence ?? 'medium');
  const [rationale, setRationale] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const fieldId = (name: string) => `potential-${name}${correcting ? `-${correcting.assessmentId}` : '-new'}`;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/potential`, {
        potentialTier,
        confidence,
        rationale: rationale.trim() || undefined,
        supersedesId: correcting?.assessmentId,
      });
      onRecorded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form
      className={formStyles.form}
      onSubmit={handleSubmit}
      aria-label={correcting ? 'Correct potential' : 'Record potential'}
    >
      {correcting ? (
        <p className={formStyles.perspective}>
          This records a <strong>replacement</strong> for the earlier tier of {POTENTIAL_TIER_LABEL[correcting.currentTier]}.
          The earlier assessment is kept and stays on record; it stops counting as current.
        </p>
      ) : null}

      <div className={formStyles.field}>
        <label htmlFor={fieldId('tier')} className={formStyles.label}>
          Tier
        </label>
        <select
          id={fieldId('tier')}
          className={factStyles.select}
          value={potentialTier}
          onChange={(event) => setPotentialTier(event.target.value as PotentialTier)}
          disabled={submitting}
        >
          {POTENTIAL_TIER_OPTIONS.map((tier) => (
            <option key={tier} value={tier}>
              {POTENTIAL_TIER_LABEL[tier]}
            </option>
          ))}
        </select>
      </div>

      <div className={formStyles.field}>
        <label htmlFor={fieldId('confidence')} className={formStyles.label}>
          Confidence
        </label>
        <select
          id={fieldId('confidence')}
          className={factStyles.select}
          value={confidence}
          onChange={(event) => setConfidence(event.target.value as ConfidenceLevel)}
          disabled={submitting}
        >
          {CONFIDENCE_OPTIONS.map((level) => (
            <option key={level} value={level}>
              {CONFIDENCE_COPY[level].label}
            </option>
          ))}
        </select>
      </div>

      <div className={formStyles.field}>
        <label htmlFor={fieldId('rationale')} className={formStyles.label}>
          Rationale <span className={formStyles.optional}>optional</span>
        </label>
        <textarea
          id={fieldId('rationale')}
          className={formStyles.textarea}
          rows={2}
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          disabled={submitting}
          placeholder="Your reasoning for this judgement"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Recording…' : correcting ? 'Record correction' : 'Record potential'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ReadinessForm({
  employeeId,
  targetRoleId,
  correcting,
  onRecorded,
  onClose,
}: {
  employeeId: string;
  targetRoleId: string;
  correcting?: { assessmentId: string; currentLevel: ReadinessLevel; currentConfidence: ConfidenceLevel };
  onRecorded: () => void;
  onClose: () => void;
}) {
  const [readiness, setReadiness] = useState<ReadinessLevel>(correcting?.currentLevel ?? 'developing');
  const [confidence, setConfidence] = useState<ConfidenceLevel>(correcting?.currentConfidence ?? 'medium');
  const [rationale, setRationale] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const fieldId = (name: string) => `readiness-${name}${correcting ? `-${correcting.assessmentId}` : '-new'}`;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/readiness/${targetRoleId}`, {
        readiness,
        confidence,
        rationale: rationale.trim() || undefined,
        supersedesId: correcting?.assessmentId,
      });
      onRecorded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form
      className={formStyles.form}
      onSubmit={handleSubmit}
      aria-label={correcting ? 'Correct readiness' : 'Record readiness'}
    >
      {correcting ? (
        <p className={formStyles.perspective}>
          This records a <strong>replacement</strong> for the earlier level of{' '}
          {READINESS_LEVEL_LABEL[correcting.currentLevel]}. The earlier assessment is kept and stays on record; it
          stops counting as current.
        </p>
      ) : null}

      <div className={formStyles.field}>
        <label htmlFor={fieldId('level')} className={formStyles.label}>
          Readiness
        </label>
        <select
          id={fieldId('level')}
          className={factStyles.select}
          value={readiness}
          onChange={(event) => setReadiness(event.target.value as ReadinessLevel)}
          disabled={submitting}
        >
          {READINESS_LEVEL_OPTIONS.map((level) => (
            <option key={level} value={level}>
              {READINESS_LEVEL_LABEL[level]}
            </option>
          ))}
        </select>
      </div>

      <div className={formStyles.field}>
        <label htmlFor={fieldId('confidence')} className={formStyles.label}>
          Confidence
        </label>
        <select
          id={fieldId('confidence')}
          className={factStyles.select}
          value={confidence}
          onChange={(event) => setConfidence(event.target.value as ConfidenceLevel)}
          disabled={submitting}
        >
          {CONFIDENCE_OPTIONS.map((level) => (
            <option key={level} value={level}>
              {CONFIDENCE_COPY[level].label}
            </option>
          ))}
        </select>
      </div>

      <div className={formStyles.field}>
        <label htmlFor={fieldId('rationale')} className={formStyles.label}>
          Rationale <span className={formStyles.optional}>optional</span>
        </label>
        <textarea
          id={fieldId('rationale')}
          className={formStyles.textarea}
          rows={2}
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          disabled={submitting}
          placeholder="Your reasoning for this judgement"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Recording…' : correcting ? 'Record correction' : 'Record readiness'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Withdrawal (Mission 162's Potential/Readiness Lifecycle increment) — the
 * same `active -> withdrawn` transition and "not deleted, marked, with a
 * reason" discipline `AssessmentRecords.tsx`'s own `WithdrawalForm` already
 * uses for attribute assessments. Deliberately not one shared component:
 * the confirmation copy names a different fact (a tier vs. a readiness
 * level) and the endpoint differs, and this file's own convention is
 * parallel Potential/Readiness variants throughout rather than a generic
 * abstraction over the two.
 */
function PotentialWithdrawalForm({
  employeeId,
  potential,
  onWithdrawn,
  onClose,
}: {
  employeeId: string;
  potential: PotentialAssessmentResponse;
  onWithdrawn: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const reasonIsValid = reason.trim().length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !reasonIsValid) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/potential/${potential.id}/withdrawal`, { reason: reason.trim() });
      onWithdrawn();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label="Withdraw potential assessment">
      <p className={formStyles.perspective}>
        Withdraw the <strong>{POTENTIAL_TIER_LABEL[potential.potentialTier]}</strong> potential assessment recorded{' '}
        {formatTimestamp(potential.assessedAt)}? It is not deleted — it stays on record, marked as withdrawn, with the
        reason you give. This cannot be undone.
      </p>

      <div className={formStyles.field}>
        <label htmlFor={`potential-withdrawal-reason-${potential.id}`} className={formStyles.label}>
          Reason
        </label>
        <textarea
          id={`potential-withdrawal-reason-${potential.id}`}
          className={formStyles.textarea}
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={submitting}
          placeholder="Why this assessment should no longer stand"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !reasonIsValid}>
          {submitting ? 'Withdrawing…' : 'Withdraw'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** As `PotentialWithdrawalForm`, for Readiness. */
function ReadinessWithdrawalForm({
  employeeId,
  readiness,
  onWithdrawn,
  onClose,
}: {
  employeeId: string;
  readiness: ReadinessAssessmentResponse;
  onWithdrawn: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const reasonIsValid = reason.trim().length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !reasonIsValid) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/readiness/${readiness.targetRoleId}/${readiness.id}/withdrawal`, {
        reason: reason.trim(),
      });
      onWithdrawn();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label="Withdraw readiness assessment">
      <p className={formStyles.perspective}>
        Withdraw the <strong>{READINESS_LEVEL_LABEL[readiness.readiness]}</strong> readiness assessment recorded{' '}
        {formatTimestamp(readiness.assessedAt)}? It is not deleted — it stays on record, marked as withdrawn, with the
        reason you give. This cannot be undone.
      </p>

      <div className={formStyles.field}>
        <label htmlFor={`readiness-withdrawal-reason-${readiness.id}`} className={formStyles.label}>
          Reason
        </label>
        <textarea
          id={`readiness-withdrawal-reason-${readiness.id}`}
          className={formStyles.textarea}
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={submitting}
          placeholder="Why this assessment should no longer stand"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !reasonIsValid}>
          {submitting ? 'Withdrawing…' : 'Withdraw'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
